import React, { useState, useMemo, useCallback, useEffect } from "react"

import MuiModal from "../shared/mui_modal"
import {
  FormControl,
  Grid,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material"
import { deploymentTypeLabels, installTypeLabels } from "./groups_paged_table"
import { apps as appsClient } from "../../ajax_clients"
import allSettled from "promise.allsettled"
import IntuneChip from "@/components/groups/shared/intune_chip"

function supportsDepOnlyInstallType(app) {
  if (!app?.installationChannels?.length) return false
  if (!app.installationChannels.includes("standard")) return false
  // NOTE: This logic is also defined in frontend/src/components/groups/tabs/apps/hooks.tsx make sure to update it there too if you're making changes here
  return app.platformSupport === "macOS" && app.appType === "Custom"
}

function installTypesForApp(app) {
  const standard = supportsDepOnlyInstallType(app)
    ? ["auto", "manual", "dep_only"]
    : ["auto", "manual"]
  return {
    standard,
    munki: ["managed", "self_serve", "default_installs", "managed_updates"],
  }
}

export default function AssignGroupsModal({ show, onClose, onSuccess, appId }) {
  const [app, setApp] = useState({})
  const [groups, setGroups] = useState([])
  const [selectedGroupIds, setSelectedGroupIds] = useState([])
  const [deploymentType, setDeploymentType] = useState("")
  const [installType, setInstallType] = useState("")
  const [loading, setLoading] = useState(true)
  const [policy, setPolicy] = useState(null)

  useEffect(() => {
    if (!show) return

    setLoading(true)
    const loadApp = appsClient.read(appId)
    const loadGroups = appsClient.readGroupsForAssignments(appId)

    allSettled([loadApp, loadGroups]).then(responses => {
      const [appResponse, groupsResponse] = responses
      const loadedApp = appResponse.value.data
      setApp(loadedApp)
      setGroups(groupsResponse.value.data)
      setDeploymentType(loadedApp.installationChannels[0])
      setInstallType(
        installTypesForApp(loadedApp)[loadedApp.installationChannels[0]][0],
      )
      setPolicy(groupsResponse.value.policy)
      setLoading(false)
    })
  }, [
    appId,
    show,
    setLoading,
    setApp,
    setGroups,
    setDeploymentType,
    setInstallType,
    setPolicy,
  ])

  const groupOptions = useMemo(() => {
    if (loading) return []

    return groups.map(group => ({
      id: group.id,
      label: group.name,
      usedForIntune: group.usedForIntune,
    }))
  }, [groups, loading])

  const handleChange = useCallback(
    (_event, newValue) => {
      if (!newValue) {
        setSelectedGroupIds([])
        return
      }

      setSelectedGroupIds(newValue.map(group => group.id))
    },
    [setSelectedGroupIds],
  )

  const handleDeploymentTypeChange = useCallback(
    event => {
      const nextDeploymentType = event.target.value
      setDeploymentType(nextDeploymentType)
      setInstallType(installTypesForApp(app)[nextDeploymentType][0])
    },
    [app, setDeploymentType, setInstallType],
  )

  const handleInstallTypeChange = useCallback(
    event => {
      setInstallType(event.target.value)
    },
    [setInstallType],
  )

  const optionsForInstallType = useMemo(() => {
    if (!deploymentType) return []

    return installTypesForApp(app)[deploymentType].map(type => ({
      value: type,
      label: installTypeLabels[type],
    }))
  }, [app, deploymentType])

  const optionsForDeploymentType = useMemo(() => {
    if (!deploymentType) return []
    if (!app) return []

    return (
      app?.installationChannels?.map(channel => ({
        value: channel,
        label: deploymentTypeLabels[channel],
      })) || []
    )
  }, [app, deploymentType])

  const handleCommit = useCallback(() => {
    setLoading(true)
    appsClient
      .createGroupAssignment(
        appId,
        selectedGroupIds,
        deploymentType,
        installType,
      )
      .then(() => {
        setSelectedGroupIds([])
        setDeploymentType("")
        setInstallType("")
        onSuccess()
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [
    appId,
    selectedGroupIds,
    deploymentType,
    installType,
    setLoading,
    onClose,
    onSuccess,
  ])

  if (!show) return null

  return (
    <MuiModal
      titleText="Assign Groups"
      buttonText="Assign"
      onClose={onClose}
      onCommit={handleCommit}
    >
      <Grid
        item
        xs={12}
        container
        alignItems="center"
        spacing={2}
        sx={{ minHeight: "120px" }}
      >
        <>
          <Grid item xs={2}>
            <InputLabel id="deployment-type-select-label">
              Install Method
            </InputLabel>
          </Grid>
          <Grid item xs={9}>
            <FormControl fullWidth size="small">
              <Select
                labelId="deployment-type-select-label"
                variant="standard"
                value={deploymentType}
                label="Install Method"
                onChange={handleDeploymentTypeChange}
                disabled={loading}
              >
                {optionsForDeploymentType.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <InputLabel id="install-type-select-label">Install Type</InputLabel>
          </Grid>
          <Grid item xs={9}>
            <FormControl fullWidth size="small">
              <Select
                labelId="install-type-select-label"
                variant="standard"
                value={installType}
                label="Install Method"
                onChange={handleInstallTypeChange}
                disabled={loading}
              >
                {optionsForInstallType.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <InputLabel id="group-select-label">Groups</InputLabel>
          </Grid>
          <Grid item xs={9}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="group-select"
                aria-labelledby="group-select-label"
                options={groupOptions}
                getOptionLabel={option => option.label}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <div>
                      {option.label}
                      {option.usedForIntune && <IntuneChip sx={{ ml: 1 }} />}
                    </div>
                  </li>
                )}
                getOptionDisabled={option =>
                  option.usedForIntune && !policy?.modifyIntune
                }
                disableCloseOnSelect
                loading={loading}
                onChange={handleChange}
                disabled={loading}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Select groups"
                  />
                )}
              />
            </FormControl>
          </Grid>
        </>
      </Grid>
    </MuiModal>
  )
}
